
import { useHMIEditorStore } from './store/hmiEditorStore';
import { useProjectStore } from '@/store/projectStore';
import { PropertyEditor } from '@/ui/properties/PropertyEditor';
import type { PropertyValue } from '@/core/types';
import { Button } from '@/ui/components/Button';
import styles from '@/ui/panels/PropertiesPanel.module.css';

export function HMIPropertiesPanel() {
  const { activeScreenId, selectedComponentIds, updateComponent, removeComponent } = useHMIEditorStore();
  const hmiProject = useProjectStore(s => s.project.hmi);
  
  if (selectedComponentIds.length !== 1 || !activeScreenId) {
    return null;
  }

  const selectedId = selectedComponentIds[0] as string;
  const screen = hmiProject.screens.find(s => s.id === activeScreenId);
  const component = screen?.components.find(c => c.id === selectedId);

  if (!component) return null;

  const handlePropChange = (key: string, value: PropertyValue) => {
    updateComponent(selectedId, { props: { ...component.props, [key]: value } });
  };

  const handleBindingChange = (key: string, value: PropertyValue) => {
    updateComponent(selectedId, { binding: { ...component.binding, [key]: value } });
  };

  return (
    <div className={styles.content}>
      <section aria-label="General" className={styles.section}>
        <h3 className={styles.sectionTitle}>General</h3>
        <dl className={styles.facts}>
          <dt>Type</dt>
          <dd>{component.type}</dd>
          <dt>ID</dt>
          <dd className={styles.mono}>{component.id}</dd>
        </dl>
      </section>

      <section aria-label="Layout" className={styles.section}>
        <h3 className={styles.sectionTitle}>Layout</h3>
        <div className={styles.editors}>
          <PropertyEditor
            definition={{ key: 'x', labelKey: 'hmi.prop.x', kind: 'number' }}
            value={component.x}
            disabled={false}
            onChange={(_, val) => updateComponent(selectedId, { x: Number(val) })}
          />
          <PropertyEditor
            definition={{ key: 'y', labelKey: 'hmi.prop.y', kind: 'number' }}
            value={component.y}
            disabled={false}
            onChange={(_, val) => updateComponent(selectedId, { y: Number(val) })}
          />
          <PropertyEditor
            definition={{ key: 'width', labelKey: 'hmi.prop.width', kind: 'number' }}
            value={component.width}
            disabled={false}
            onChange={(_, val) => updateComponent(selectedId, { width: Number(val) })}
          />
          <PropertyEditor
            definition={{ key: 'height', labelKey: 'hmi.prop.height', kind: 'number' }}
            value={component.height}
            disabled={false}
            onChange={(_, val) => updateComponent(selectedId, { height: Number(val) })}
          />
        </div>
      </section>

      <section aria-label="Properties" className={styles.section}>
        <h3 className={styles.sectionTitle}>Properties</h3>
        <div className={styles.editors}>
          <PropertyEditor
            definition={{ key: 'label', labelKey: 'hmi.prop.label', kind: 'text' }}
            value={(component.props.label as string) || ''}
            disabled={false}
            onChange={(k, v) => handlePropChange(k, v)}
          />
          <PropertyEditor
            definition={{ key: 'color', labelKey: 'hmi.prop.color', kind: 'text' }}
            value={(component.props.color as string) || '#cccccc'}
            disabled={false}
            onChange={(k, v) => handlePropChange(k, v)}
          />
          {component.type === 'PushButton' && (
            <PropertyEditor
              definition={{ 
                key: 'behavior', 
                labelKey: 'hmi.prop.behavior', 
                kind: 'select',
                options: [
                  { value: 'momentary', labelKey: 'hmi.prop.momentary' },
                  { value: 'toggle', labelKey: 'hmi.prop.toggle' }
                ]
              }}
              value={(component.props.behavior as string) || 'momentary'}
              disabled={false}
              onChange={(k, v) => handlePropChange(k, v)}
            />
          )}
        </div>
      </section>

      <section aria-label="Data Binding" className={styles.section}>
        <h3 className={styles.sectionTitle}>Data Binding</h3>
        <div className={styles.editors}>
          <PropertyEditor
            definition={{ 
              key: 'type', 
              labelKey: 'hmi.prop.bindingType', 
              kind: 'select',
              options: [
                { value: 'NONE', labelKey: 'hmi.prop.none' },
                { value: 'PLC', labelKey: 'hmi.prop.plc' },
                { value: 'MODBUS', labelKey: 'hmi.prop.modbus' },
                { value: 'ALARM', labelKey: 'hmi.prop.alarm' }
              ]
            }}
            value={component.binding.type}
            disabled={false}
            onChange={(k, v) => handleBindingChange(k, v)}
          />
          {component.binding.type !== 'NONE' && (
            <PropertyEditor
              definition={{ key: 'address', labelKey: 'hmi.prop.address', kind: 'text' }}
              value={component.binding.address}
              disabled={false}
              onChange={(k, v) => handleBindingChange(k, v)}
            />
          )}
          {component.binding.type === 'PLC' && (component.type === 'LinearGauge' || component.type === 'NumericDisplay') && (
            <>
              <PropertyEditor
                definition={{ key: 'scaleMin', labelKey: 'hmi.prop.scaleMin', kind: 'number' }}
                value={component.binding.scaleMin ?? 0}
                disabled={false}
                onChange={(k, v) => handleBindingChange(k, Number(v))}
              />
              <PropertyEditor
                definition={{ key: 'scaleMax', labelKey: 'hmi.prop.scaleMax', kind: 'number' }}
                value={component.binding.scaleMax ?? 100}
                disabled={false}
                onChange={(k, v) => handleBindingChange(k, Number(v))}
              />
            </>
          )}
        </div>
      </section>

      <div style={{ marginTop: '20px' }}>
        <Button
          variant="primary"
          onClick={() => {
            removeComponent(selectedId);
          }}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          Delete Component
        </Button>
      </div>
    </div>
  );
}
